import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Move3d } from "lucide-react";
import WattZapLogo from "./WattZapLogo";
import { usePrefersReducedMotion } from "./journey/hooks";

const TONES = {
  cyan: "#00D4FF", teal: "#2DD4BF", green: "#21F59B",
  gold: "#FFC857", red: "#F87171", blue: "#60A5FA", navy: "#0A2540",
};

const W = 800, H = 450;
const curve = (a, b) => {
  const mx = (a.x + b.x) / 2;
  return `M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
};

function Node({ node, badge, highlighted, focused, onFocus }) {
  const Icon = node.icon;
  const tone = TONES[node.tone || "cyan"];
  return (
    <button
      type="button"
      data-testid={`viz-node-${node.id}`}
      onClick={() => onFocus(node.id)}
      aria-label={`${node.label} — select for details`}
      className="absolute flex flex-col items-center gap-1 focus:outline-none group"
      style={{
        left: `${(node.x / W) * 100}%`, top: `${(node.y / H) * 100}%`,
        transform: "translate(-50%,-50%) translateZ(26px)",
      }}
    >
      {badge && (
        <span
          className="px-2 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap border"
          style={{ color: TONES[badge.tone], borderColor: `${TONES[badge.tone]}66`, backgroundColor: `${TONES[badge.tone]}1a` }}
        >
          {badge.text}
        </span>
      )}
      <span
        className={`flex items-center justify-center rounded-xl border transition-all duration-200 group-hover:scale-110 group-focus-visible:ring-2 group-focus-visible:ring-[#00D4FF] ${node.big ? "w-14 h-14" : "w-11 h-11"}`}
        style={{
          background: "linear-gradient(160deg, #0E2E4F 0%, #0A2540 60%, #071A2D 100%)",
          borderColor: focused ? TONES.cyan : highlighted ? `${tone}99` : "rgba(255,255,255,0.14)",
          boxShadow: focused
            ? `0 0 0 2px ${TONES.cyan}55, 0 10px 24px -8px rgba(0,0,0,.6)`
            : highlighted
              ? `0 0 18px ${tone}44, 0 10px 24px -8px rgba(0,0,0,.6)`
              : "0 10px 24px -8px rgba(0,0,0,.6)",
          color: tone,
        }}
      >
        {node.wattzap ? <WattZapLogo variant="mark" entrance={false} height={node.big ? 30 : 24} /> : <Icon size={node.big ? 22 : 17} />}
      </span>
      <span className="text-[10px] leading-tight text-white/75 max-w-[92px] text-center">{node.label}</span>
    </button>
  );
}

export default function Product3DVisual({ scene, testId = "product-3d-visual" }) {
  const reduced = usePrefersReducedMotion();
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  const [stateIdx, setStateIdx] = useState(0);
  const [focus, setFocus] = useState(null);
  const [tilt, setTilt] = useState({ x: 11, y: -7 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setReady(true); setInView(e.isIntersecting); },
      { rootMargin: "250px", threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, tx: tilt.x, ty: tilt.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current || e.pointerType !== "mouse") return;
    const d = dragRef.current;
    setTilt({
      x: Math.max(2, Math.min(20, d.tx - (e.clientY - d.sy) * 0.08)),
      y: Math.max(-16, Math.min(16, d.ty + (e.clientX - d.sx) * 0.08)),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  const state = scene.states?.[stateIdx];
  const nodeById = Object.fromEntries(scene.nodes.map((n) => [n.id, n]));
  const focusHotspot = focus && scene.hotspots?.find((h) => h.node === focus);
  const animating = ready && inView && !paused && !reduced;

  return (
    <div ref={wrapRef} data-testid={testId} className="w-full">
      <div
        role="img"
        aria-label={`${scene.title}. ${scene.description}`}
        className="relative rounded-2xl border border-white/10 bg-[#061523] p-3 select-none"
        style={{ perspective: "1100px", touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
          {!ready ? (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">{scene.title}</div>
          ) : (
            <div
              className="absolute inset-0"
              style={{
                transformStyle: "preserve-3d",
                transform: reduced ? "rotateX(9deg)" : `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                transition: dragRef.current ? "none" : "transform .35s ease-out",
              }}
            >
              {/* floor */}
              <div
                className="absolute inset-0 rounded-xl"
                aria-hidden="true"
                style={{
                  background: "radial-gradient(ellipse at 50% 40%, rgba(0,212,255,0.07) 0%, rgba(10,37,64,0.6) 55%, rgba(6,21,35,0.9) 100%)",
                  backgroundSize: "cover",
                  boxShadow: "inset 0 0 40px rgba(0,0,0,0.4)",
                }}
              >
                <div className="absolute inset-0 rounded-xl opacity-[0.14]" style={{
                  backgroundImage: "linear-gradient(rgba(0,212,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,.5) 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }} />
              </div>
              {/* flows */}
              <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" aria-hidden="true" style={{ transform: "translateZ(12px)" }} preserveAspectRatio="none">
                {scene.flows.map((f, i) => {
                  const a = nodeById[f.from], b = nodeById[f.to];
                  if (!a || !b) return null;
                  const d = curve(a, b);
                  const tone = TONES[f.tone || "cyan"];
                  const hot = state?.highlight?.includes(f.from) && state?.highlight?.includes(f.to);
                  return (
                    <g key={i}>
                      <path d={d} fill="none" stroke={tone} strokeOpacity={hot ? 0.85 : 0.32} strokeWidth={hot ? 2.4 : 1.6} strokeDasharray="6 7" />
                      {animating && [0, 1].map((p) => (
                        <circle key={p} r="3.2" fill={tone}>
                          <animateMotion dur={`${3 + (i % 3)}s`} begin={`${p * 1.6 + i * 0.35}s`} repeatCount="indefinite" path={d} />
                        </circle>
                      ))}
                    </g>
                  );
                })}
              </svg>
              {/* nodes */}
              <div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
                {scene.nodes.map((n) => (
                  <Node
                    key={n.id}
                    node={n}
                    badge={state?.badges?.[n.id]}
                    highlighted={state?.highlight?.includes(n.id)}
                    focused={focus === n.id}
                    onFocus={(id) => setFocus(focus === id ? null : id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        {/* controls */}
        <div className="mt-2 flex items-center justify-between gap-2 px-1">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] text-white/40"><Move3d size={12} /> Drag to rotate · tap components</span>
          <span className="sm:hidden text-[10px] text-white/40">Tap components for details</span>
          <button
            type="button"
            data-testid="viz-play-pause"
            onClick={() => setPaused((v) => !v)}
            aria-label={paused ? "Play animation" : "Pause animation"}
            className="inline-flex items-center gap-1.5 text-[10px] text-white/60 hover:text-white border border-white/15 rounded-full px-2.5 py-1 transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none"
          >
            {paused || reduced ? <Play size={11} /> : <Pause size={11} />} {paused || reduced ? "Play" : "Pause"}
          </button>
        </div>
      </div>

      {/* state toggles */}
      {scene.states?.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="viz-state-toggles">
          {scene.states.map((s, i) => (
            <button
              key={s.id}
              type="button"
              data-testid={`viz-state-${s.id}`}
              onClick={() => { setStateIdx(i); setFocus(null); }}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none ${i === stateIdx ? "bg-wz-gradient text-[#071A2D] border-transparent" : "border-white/20 text-white/70 hover:text-white"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* info panel */}
      <div data-testid="viz-info-panel" className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 min-h-[60px]">
        {focusHotspot ? (
          <>
            <div className="text-sm font-medium text-[#00D4FF]">{focusHotspot.title}</div>
            <p className="mt-1 text-xs text-white/70 leading-relaxed">{focusHotspot.body}</p>
          </>
        ) : (
          <p className="text-xs text-white/70 leading-relaxed">{state?.note || scene.description}</p>
        )}
      </div>
    </div>
  );
}
